import { useAppSelector } from '../../../reducers';
import { checkoutProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { currentSkuIdForProductSelector } from '../../../selectors/ProductsSelectors';
import { commonPurchaseMotionsSelector, purchaseMotionsSelector, talkToSalesTypeSelector } from '../../../selectors/PurchaseMotionSelectors';

/**
 * Hook to determine which purchase motions to display for HubSpot Credits tiles.
 *
 * USE WITH CAUTION:
 * - This hook is used to determine which purchase motions to display for HubSpot Credits tiles.
 * - It should only be used in the HubSpot Credits tile component.
 *
 * @param unlockHubSpotCreditsProductConfigurations - Product configurations that unlock HubSpot Credits
 * @param location - Location of the credits tile
 * @returns Purchase motions to display
 */
export const usePurchaseMotionsForHubSpotCreditsTile = ({
  unlockHubSpotCreditsProductConfigurations = [],
  location
}) => {
  const talkToSalesType = useAppSelector(talkToSalesTypeSelector);

  // PURCHASE MOTIONS FOR CREDITS
  const creditsSkuId = useAppSelector(state => currentSkuIdForProductSelector(state, {
    apiName: 'hubspot-credits-committed'
  }));
  const creditsPurchaseMotions = useAppSelector(state => {
    if (!creditsSkuId) {
      return [];
    }
    return purchaseMotionsSelector(state, {
      skuId: creditsSkuId,
      location
    });
  });

  // PURCHASE MOTIONS FOR UNLOCK PRODUCTS
  const unlockHubSpotCreditsCheckoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations: unlockHubSpotCreditsProductConfigurations,
    includeOnboarding: false
  }));
  const unlockPurchaseMotions = useAppSelector(state => {
    const skuIdsToUse = unlockHubSpotCreditsCheckoutProductConfigurations.map(({
      skuId
    }) => skuId);
    const commonPurchaseMotions = commonPurchaseMotionsSelector(state, {
      skuIds: skuIdsToUse,
      location
    });
    if (!commonPurchaseMotions.length) {
      return [talkToSalesType];
    }
    return commonPurchaseMotions;
  });

  // COMBINED EVALUATION: intersection of unlock and credits purchase motions
  if (unlockHubSpotCreditsProductConfigurations.length === 0) {
    return creditsPurchaseMotions;
  }
  const combinedPurchaseMotions = unlockPurchaseMotions.filter(motion => {
    if (motion === 'TALK_TO_SALES' || motion === 'TALK_TO_SALES_FALLBACK') {
      return creditsPurchaseMotions.includes('TALK_TO_SALES') || creditsPurchaseMotions.includes('TALK_TO_SALES_FALLBACK');
    }
    return creditsPurchaseMotions.includes(motion);
  });
  return combinedPurchaseMotions.length > 0 ? combinedPurchaseMotions : [talkToSalesType];
};